import { useState, useEffect } from "react";
import parseVttSubtitles from "utils/parse-vtt-subtitles";
import * as captionConsts from "core/constants/captions";
import { Subtitle, Fragment } from "core/models";
import { useNotifications } from "ui/context/NotificationsContext";

const checkSubtitlesOverlapping = (a: Subtitle, b: Subtitle): boolean => {
    if (b.startTime < a.startTime) {
        return b.endTime > a.startTime;
    }
    return b.startTime < a.endTime;
};

interface HookReturn {
    subtitles: Subtitle[];
    activeSubtitle: Subtitle | undefined;
    parsePending: boolean;
    setActiveSubtitle(activeSubtitle: Subtitle | undefined): void;
    addSubtitle(index: number, startTime: number): void;
    changeSubtitleTime(key: "startTime" | "endTime", value: number): void;
    deleteSubtitle(index: number): void;
    checkEnoughDistance(index: number): boolean;
    saveSubtitlecardChange(changedSubtitle: Subtitle, subIdx: number): void;
}

const useCaptions = (fragment: Fragment, videoDuration: number): HookReturn => {
    const { addNotification } = useNotifications();
    const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
    const [activeSubtitle, setActiveSubtitle] = useState<
        Subtitle | undefined
    >();
    const [parsePending, setParsePending] = useState<boolean>(true);

    const checkCaptionOverlap = (changedSubtitle: Subtitle): boolean =>
        subtitles.some((subtitle) => {
            if (subtitle.id === changedSubtitle.id) {
                return false;
            }
            return checkSubtitlesOverlapping(changedSubtitle, subtitle);
        });

    const checkCaptionConflict = (subtitle: Subtitle): boolean =>
        subtitle.startTime >= subtitle.endTime;

    const checkCaptionTooShort = (subtitle: Subtitle) =>
        subtitle.endTime - subtitle.startTime < captionConsts.minLength;

    const checkForSubtitleErrors = (subtitle: Subtitle): string | undefined => {
        const conflict = checkCaptionConflict(subtitle);
        const overlap = checkCaptionOverlap(subtitle);
        const tooShort = checkCaptionTooShort(subtitle);
        if (conflict) {
            return "Start can not be after end";
        }
        if (overlap) {
            return "Overlapping timecodes";
        }
        if (tooShort) {
            return "Caption should be a minumum of 0.5s long";
        }
        return undefined;
    };

    const saveSubtitlecardChange = (
        changedSubtitle: Subtitle,
        subIdx: number,
    ): void => {
        const newChangedSubtitle = { ...changedSubtitle };
        newChangedSubtitle.error = checkForSubtitleErrors(newChangedSubtitle);
        const newSubtitles = [...subtitles];
        newSubtitles.splice(subIdx, 1, newChangedSubtitle);
        setSubtitles(newSubtitles);
    };

    const calcDistanceByIndex = (index: number): number => {
        let nextStartTime = videoDuration;
        if (index < subtitles.length) {
            nextStartTime = subtitles[index].startTime;
        }
        const prevEndTime = index > 0 ? subtitles[index - 1].endTime : 0;
        return nextStartTime - prevEndTime;
    };

    const checkEnoughDistance = (index: number): boolean => {
        if (subtitles.length === 0) {
            return true;
        }
        const distance = calcDistanceByIndex(index);
        return distance >= captionConsts.minLength;
    };

    const changeSubtitleTime = (
        key: "startTime" | "endTime",
        value: number,
    ): void => {
        if (!activeSubtitle) {
            return;
        }
        const newSubtitles = [...subtitles].map((sub) => {
            if (sub.id === activeSubtitle.id) {
                const subClone = { ...sub };
                subClone[key] = value;
                subClone.error = checkForSubtitleErrors(subClone);
                return subClone;
            }
            return sub;
        });
        const activeSubtitleClone = { ...activeSubtitle };
        activeSubtitleClone[key] = value;
        setActiveSubtitle(activeSubtitleClone);
        setSubtitles(newSubtitles);
    };

    const bestEndTimeBasedOnIndex = (index: number): number => {
        const distance = calcDistanceByIndex(index);
        let bestLength = captionConsts.defaultLength;
        if (distance < captionConsts.defaultLength) {
            bestLength = distance;
        }
        const endTime = subtitles[index - 1].endTime + bestLength;

        // Ensure endTime provides at least the minimum length
        if (endTime - subtitles[index - 1].endTime < captionConsts.minLength) {
            return Math.min(
                subtitles[index - 1].endTime + captionConsts.minLength,
                videoDuration,
            );
        }

        return Math.min(endTime, videoDuration);
    };

    const checkStartTimeSurroundings = (newCaptionStartTime: number) => {
        const closestTime = (key: "startTime" | "endTime") =>
            subtitles.reduce((currentClosest, subtitle) => {
                let validityCheck = subtitle.startTime > newCaptionStartTime;
                if (key === "endTime") {
                    validityCheck = subtitle.endTime < newCaptionStartTime;
                }
                if (
                    validityCheck &&
                    Math.abs(newCaptionStartTime - subtitle[key]) <
                        Math.abs(newCaptionStartTime - currentClosest[key])
                ) {
                    return subtitle;
                }

                return currentClosest;
            }, subtitles[0]);
        return [closestTime("startTime"), closestTime("endTime")];
    };

    const bestEndTimeBasedOnStartTime = (startTime: number): number => {
        if (!subtitles.length) {
            const endTime = Math.min(
                startTime + captionConsts.defaultLength,
                videoDuration,
            );
            return endTime;
        }

        const [closestStartTime] = checkStartTimeSurroundings(startTime);

        let endTime = startTime + captionConsts.defaultLength;

        if (closestStartTime && endTime > closestStartTime.startTime) {
            endTime = closestStartTime.startTime - 0.1;
        }
        if (endTime > videoDuration) {
            endTime = videoDuration;
        }

        // Ensure endTime provides at least the minimum length
        if (endTime <= startTime + captionConsts.minLength) {
            endTime = Math.min(
                startTime + captionConsts.minLength,
                videoDuration,
            );
        }
        return endTime;
    };

    const addSubtitle = (index = 0, startTime = 0): void => {
        const newSubtitles = [...subtitles];
        let newSubtitle: Subtitle;

        if (index === 0) {
            // Adding at the start
            newSubtitle = {
                id: Math.round(Math.random() * 100000),
                startTime,
                endTime: bestEndTimeBasedOnStartTime(startTime),
                text: "",
            };
        } else if (index >= subtitles.length) {
            // Adding at the end
            const prevEndTime = subtitles[subtitles.length - 1].endTime;
            newSubtitle = {
                id: Math.round(Math.random() * 100000),
                startTime: prevEndTime + 0.1,
                endTime: Math.min(
                    prevEndTime + 0.1 + captionConsts.defaultLength,
                    videoDuration,
                ),
                text: "",
            };
        } else {
            // Adding in between
            const prevEndTime = subtitles[index - 1].endTime;
            newSubtitle = {
                id: Math.round(Math.random() * 100000),
                startTime: prevEndTime + 0.1,
                endTime: bestEndTimeBasedOnIndex(index),
                text: "",
            };
        }

        if (
            newSubtitle.endTime > videoDuration ||
            newSubtitle.endTime - newSubtitle.startTime <
                captionConsts.minLength ||
            checkCaptionOverlap(newSubtitle)
        ) {
            addNotification(
                "error",
                "Space Error",
                "Not enough space for your caption",
                2000,
            );
            return;
        }
        newSubtitle.error = checkForSubtitleErrors(newSubtitle);
        newSubtitles.splice(index, 0, newSubtitle);
        setSubtitles(newSubtitles);
        setActiveSubtitle(newSubtitle);
    };

    const deleteSubtitle = (index: number): void => {
        if (subtitles[index].id === activeSubtitle?.id) {
            setActiveSubtitle(undefined);
        }
        const newSubtitles = [...subtitles];
        newSubtitles.splice(index, 1);
        setSubtitles(newSubtitles);
    };

    useEffect(() => {
        fragment.getSubtitlesAsString().then((subtitleString) => {
            setParsePending(false);
            if (subtitleString) {
                setSubtitles(parseVttSubtitles(subtitleString));
            }
        });
    }, []);

    useEffect(() => {
        if (subtitles.length === 0 && videoDuration !== 0) {
            addSubtitle();
        }
    }, [videoDuration]);

    return {
        subtitles,
        activeSubtitle,
        parsePending,
        setActiveSubtitle,
        addSubtitle,
        changeSubtitleTime,
        deleteSubtitle,
        checkEnoughDistance,
        saveSubtitlecardChange,
    };
};
export default useCaptions;
